import { useEffect, useMemo, useRef, useState } from "react";
import type { Dispatch, MutableRefObject, SetStateAction } from "react";
import { TypeConf } from "./types";
import { OptionsObject, SnackbarMessage, useSnackbar } from "notistack";
import { AxiosPromise, AxiosResponse } from "axios";
import { FormError, manageErrors, removePreviousErrors } from "./form_errors";
import { v4 as uuidv4 } from "uuid";
import { NavigateFunction, useNavigate, useParams } from "react-router-dom";
import { FormButtonProps } from "./form_inputs/form_button";
import { parsePathId } from "./form_utils";

export interface Identifiable {
    id?: number;
}

type ModelIdFetcher = () => number | undefined;
type Promisable<T> = T | Promise<T> | AxiosPromise<T>;

export type FieldChangeHandler<T> = (value: any, model: T) => void;

export interface UseFormProps<T> {
    /**
     * Función que indica de dónde coger el id.
     * Si no se indica, lo lee de los Params de la ruta.
     */
    modelIdFetcher?: ModelIdFetcher;

    /** Nombre del param de ruta si es distinto de 'id'. */
    idParamName?: string;

    /** Crea una instancia vacía para formularios de alta. */
    factory: () => Promisable<T>;

    /** Persiste el bean en el servidor. */
    updater: (bean: T) => Promisable<T>;

    /** Carga un registro existente por id. */
    fetcher?: (id: number) => Promisable<T>;

    /** Elimina o da de baja un registro. */
    remover?: (id: number) => Promisable<void>;

    /** Ejecutado tras cargar datos del servidor. */
    postLoad?: (bean: T) => void;

    /** Ejecutado tras guardar correctamente. */
    postUpdate?: (bean: T) => void;

    /**
     * Validación previa al guardado.
     * - `true`   → permitir guardado
     * - `false`  → cancelar sin mensaje
     * - `string` → cancelar y mostrar el string como aviso
     * - `Promise<boolean|string>` → validación asíncrona
     */
    preUpdate?: (bean: T) => boolean | string | Promise<boolean | string>;

    /** Ejecutado tras eliminar correctamente. */
    postRemove?: (bean: T) => void;

    /**
     * Listener global: se llama cada vez que cualquier input del formulario
     * cambia su valor. Útil para reacciones cruzadas entre campos.
     *
     * @example
     * onFieldChange: (field, value, model) => {
     *   if (field === 'customer') loadInvoices(value.id);
     * }
     */
    onFieldChange?: (field: string, value: any, model: T) => void;

    /** Configuración del botón Guardar (por defecto: label "Guardar"). */
    defaultAction?: FormButtonProps;

    /** Configuración del botón Borrar (por defecto: label "Borrar", solo si hay `remover`). */
    removeAction?: FormButtonProps;

    extraActions?: FormButtonProps[];
}

// ─── Contexto interno pasado de useForm → FormModel ──────────────────────────

interface FormModelCtx<T> {
    formId: string;
    configRef: MutableRefObject<UseFormProps<T>>;
    fieldsRef: MutableRefObject<Map<string, () => any>>;
    fieldSetsRef: MutableRefObject<Map<string, (v: any) => void>>;
    modelRef: MutableRefObject<T | undefined>;
    setModelState: Dispatch<SetStateAction<T | undefined>>;
    unmountedRef: MutableRefObject<boolean>;
    navigateRef: MutableRefObject<NavigateFunction>;
    snackbarRef: MutableRefObject<(msg: SnackbarMessage, opts?: OptionsObject) => void>;
    paramsRef: MutableRefObject<Record<string, string | undefined>>;
}

// ─── El hook: todos los hooks de React viven aquí ────────────────────────────

export function useForm<T extends Identifiable>(config: UseFormProps<T>): FormModel<T> {
    const formId = useMemo(() => uuidv4(), []);
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const params = useParams();

    const [model, setModel] = useState<T | undefined>(undefined);
    const unmountedRef = useRef(false);

    // Refs "siempre frescos": se actualizan en cada render para que
    // los métodos de FormModel nunca lean closures obsoletas.
    const modelRef = useRef<T | undefined>(undefined);
    modelRef.current = model;

    const navigateRef = useRef<NavigateFunction>(navigate);
    navigateRef.current = navigate;

    const snackbarRef = useRef(enqueueSnackbar);
    snackbarRef.current = enqueueSnackbar;

    const configRef = useRef(config);
    configRef.current = config;

    const paramsRef = useRef<Record<string, string | undefined>>(params);
    paramsRef.current = params;

    const fieldsRef = useRef(new Map<string, () => any>());
    const fieldSetsRef = useRef(new Map<string, (v: any) => void>());

    // Instancia estable: se crea una sola vez por montaje del componente.
    const formRef = useRef<FormModel<T>>();
    if (!formRef.current) {
        formRef.current = new FormModel<T>({
            formId,
            configRef,
            fieldsRef,
            fieldSetsRef,
            modelRef,
            setModelState: setModel,
            unmountedRef,
            navigateRef,
            snackbarRef,
            paramsRef,
        });
    }

    // Mantiene propModel.value sincronizado con el estado React para
    // que las páginas puedan leerlo en render context.
    formRef.current.propModel.value = model;

    useEffect(() => {
        // Strict Mode monta→desmonta→vuelve a montar en desarrollo.
        // Sin este reset, el cleanup del primer ciclo dejaría unmountedRef=true
        // y el guard de loadForced bloquearía el setModel del segundo ciclo.
        unmountedRef.current = false;
        formRef.current!.load();
        return () => { unmountedRef.current = true; };
    }, []);

    return formRef.current;
}

// ─── Shim de compatibilidad para propModel ───────────────────────────────────

export class PropModel<T> {
    value: T | undefined = undefined;
    // No-op: las actualizaciones reales van por FormModel.setter() / setModel()
    set(_newValue: T | undefined): void { /* legacy compatibility */ }
}

// ─── FormModel: clase pura sin hooks ─────────────────────────────────────────

export class FormModel<T extends Identifiable> {
    readonly formId: string;
    readonly propModel: PropModel<T>;

    private readonly configRef: MutableRefObject<UseFormProps<T>>;
    private readonly fieldsRef: MutableRefObject<Map<string, () => any>>;
    private readonly fieldSetsRef: MutableRefObject<Map<string, (v: any) => void>>;
    private readonly modelRef: MutableRefObject<T | undefined>;
    private readonly setModelState: Dispatch<SetStateAction<T | undefined>>;
    private readonly unmountedRef: MutableRefObject<boolean>;
    private readonly navigateRef: MutableRefObject<NavigateFunction>;
    private readonly snackbarRef: MutableRefObject<(msg: SnackbarMessage, opts?: OptionsObject) => void>;
    private readonly paramsRef: MutableRefObject<Record<string, string | undefined>>;

    // Listeners por campo: se registran vía onFieldChange() y persisten entre renders.
    private readonly fieldListeners = new Map<string, Set<FieldChangeHandler<T>>>();

    // Acciones por defecto estables (creadas una vez, no cambian entre renders).
    private readonly builtinDefaultAction: FormButtonProps;
    private readonly builtinRemoveAction: FormButtonProps;

    constructor(ctx: FormModelCtx<T>) {
        this.formId = ctx.formId;
        this.configRef = ctx.configRef;
        this.fieldsRef = ctx.fieldsRef;
        this.fieldSetsRef = ctx.fieldSetsRef;
        this.modelRef = ctx.modelRef;
        this.setModelState = ctx.setModelState;
        this.unmountedRef = ctx.unmountedRef;
        this.navigateRef = ctx.navigateRef;
        this.snackbarRef = ctx.snackbarRef;
        this.paramsRef = ctx.paramsRef;
        this.propModel = new PropModel<T>();

        this.builtinDefaultAction = { label: "Guardar", action: () => this.save() };
        this.builtinRemoveAction = { label: "Borrar", action: () => this.remove() };
    }

    // ─── Acceso público a config (fusiona defaults con config del usuario) ────

    get config(): UseFormProps<T> {
        const cfg = this.configRef.current;
        return {
            ...cfg,
            defaultAction: cfg.defaultAction ?? this.builtinDefaultAction,
            removeAction: cfg.removeAction !== undefined
                ? cfg.removeAction
                : (cfg.remover ? this.builtinRemoveAction : undefined),
        };
    }

    // ─── Registro de campos ───────────────────────────────────────────────────

    register(field: string, getter: () => any, setter: (value: any) => void): void {
        if (typeof getter !== "function") throw new Error(`${field}: getter must be a function`);
        if (typeof setter !== "function") throw new Error(`${field}: setter must be a function`);
        this.fieldsRef.current.set(field, getter);
        this.fieldSetsRef.current.set(field, setter);
    }

    unregister(field: string): void {
        this.fieldsRef.current.delete(field);
        this.fieldSetsRef.current.delete(field);
    }

    /**
     * Llamado por los inputs cuando el usuario modifica un valor.
     * Sincroniza el modelRef y dispara los listeners registrados,
     * sin provocar un re-render completo del formulario.
     */
    notifyFieldChange(field: string, value: any): void {
        const parent = FormModel.locateLastParent(this.modelRef.current, field);
        if (parent) parent[FormModel.lastNamePart(field)] = value;

        const listeners = this.fieldListeners.get(field);
        if (listeners?.size) {
            const model = this.modelRef.current as T;
            listeners.forEach(h => h(value, model));
        }

        this.configRef.current.onFieldChange?.(field, value, this.modelRef.current as T);
    }

    /**
     * Registra un listener para cuando un campo concreto cambia su valor.
     * Devuelve una función para cancelar la suscripción.
     *
     * @example
     * useEffect(() => form.onFieldChange('customer', (customer, model) => {
     *   loadInvoices(customer.id);
     * }), []);
     */
    onFieldChange(field: string, handler: FieldChangeHandler<T>): () => void {
        if (!this.fieldListeners.has(field)) {
            this.fieldListeners.set(field, new Set());
        }
        this.fieldListeners.get(field)!.add(handler);
        return () => this.fieldListeners.get(field)?.delete(handler);
    }

    // ─── Acceso al modelo ─────────────────────────────────────────────────────

    setter(newModel: T): void {
        this.setModel(newModel);
    }

    private setModel(value: T | undefined): void {
        this.modelRef.current = value;
        this.propModel.value = value;
        this.setModelState(value);
    }

    field(name: string): FormField {
        return new FormField(name, this);
    }

    value(fieldName: string): any {
        const parent = FormModel.locateLastParent(this.modelRef.current, fieldName);
        return parent?.[FormModel.lastNamePart(fieldName)];
    }

    valueSet(fieldName: string, value: any): void {
        const parent = FormModel.locateLastParent(this.modelRef.current, fieldName);
        if (parent) parent[FormModel.lastNamePart(fieldName)] = value;
    }

    /**
     * Actualiza un campo programáticamente: sincroniza el modelo,
     * notifica al input para que re-renderice, y dispara listeners.
     */
    triggerField(fieldName: string, value: any): void {
        this.valueSet(fieldName, value);
        this.fieldSetsRef.current.get(fieldName)?.(value);
        this.notifyFieldChange(fieldName, value);
    }

    // ─── Helpers de estado ────────────────────────────────────────────────────

    hasContent(): boolean {
        return this.modelRef.current != null;
    }

    hasId(): boolean {
        return typeof this.modelRef.current?.id === "number";
    }

    getId(): number | undefined {
        return this.modelRef.current?.id;
    }

    getIdOrErr(): number {
        const id = this.modelRef.current?.id;
        if (!id) throw new Error("No model id found");
        return id;
    }

    withId(func: (id: number) => void): void {
        const id = this.getId();
        if (id) func(id);
    }

    isLoading(): boolean {
        return !this.hasContent();
    }

    // ─── Ciclo de vida ────────────────────────────────────────────────────────

    load(): Promise<T> {
        const cfg = this.configRef.current;
        const getModelId = cfg.modelIdFetcher
            ?? (() => parsePathId(this.paramsRef.current, cfg.idParamName));

        let promisable: Promisable<T>;
        if (cfg.fetcher) {
            const id = getModelId();
            promisable = id ? cfg.fetcher(id) : cfg.factory();
        } else {
            promisable = cfg.factory();
        }
        return this.loadForced(promisable);
    }

    loadForced(promisable: Promisable<T>): Promise<T> {
        this.setModel(undefined);
        const result = this.resolvePromisable(promisable);
        result.then(r => {
            if (this.unmountedRef.current) return;
            this.setModel(r);
            // Propaga los valores a cada input registrado.
            this.fieldSetsRef.current.forEach((setter, fieldName) => {
                const parent = FormModel.locateLastParent(r, fieldName);
                setter(parent?.[FormModel.lastNamePart(fieldName)]);
            });
            this.configRef.current.postLoad?.(r);
        });
        return result;
    }

    save(): Promise<T> {
        removePreviousErrors();
        const model = this.updateModel();
        return this.runPreUpdate(model).then(() => {
            const result = this.resolvePromisable(this.configRef.current.updater(model));
            result
                .then(r => {
                    this.configRef.current.postUpdate?.(r);
                    this.setModel(r);
                    this.showMessageOk();
                })
                .catch(err => this.showMessageErr(err));
            return result;
        });
    }

    private runPreUpdate(model: T): Promise<void> {
        const preUpdate = this.configRef.current.preUpdate;
        if (!preUpdate) return Promise.resolve();

        return Promise.resolve(preUpdate(model)).then(result => {
            if (result === true) return;
            const msg = typeof result === "string" ? result : "Operación cancelada";
            this.snackbarRef.current(msg, { variant: "warning" });
            return Promise.reject(msg);
        });
    }

    remove(): Promise<void> {
        const modelId = this.modelRef.current?.id;
        const remover = this.configRef.current.remover;
        if (!remover || !modelId) {
            console.error("remove() called without remover or model id");
            return Promise.reject("No remover or model id");
        }
        if (!confirm("¿Seguro que desea borrar el registro?")) return Promise.resolve();

        return this.resolvePromisable(remover(modelId)).then(() => {
            if (this.configRef.current.postRemove && this.modelRef.current) {
                this.configRef.current.postRemove(this.modelRef.current);
            } else {
                this.navigateRef.current(-1);
            }
        });
    }

    // ─── Internos ─────────────────────────────────────────────────────────────

    private updateModel(): T {
        const newBean = { ...this.modelRef.current } as T;
        this.fieldsRef.current.forEach((getter, fieldName) => {
            if (typeof getter !== "function") throw new Error(`${fieldName}: getter is not a function`);
            const parent = FormModel.locateLastParent(newBean, fieldName);
            parent[FormModel.lastNamePart(fieldName)] = getter();
        });
        this.setModel(newBean);
        return newBean;
    }

    private static lastNamePart(fieldName: string): string {
        const i = fieldName.lastIndexOf(".");
        return i >= 0 ? fieldName.slice(i + 1) : fieldName;
    }

    private static locateLastParent(entity: any, fieldName: string): any {
        if (!entity) entity = {};
        const path = fieldName.split(".");
        let root = entity;
        for (let i = 0; i < path.length - 1; i++) {
            const p = path[i];
            if (!root[p]) root[p] = {};
            root = root[p];
        }
        return root;
    }

    private resolvePromisable<K>(promisable: Promisable<K>): Promise<K> {
        return new Promise<K>((resolve, reject) => {
            if (promisable && (promisable as any)["then"]) {
                (promisable as Promise<K | AxiosResponse<K>>).then(resp => {
                    // Detectar AxiosResponse por .config (propiedad exclusiva de Axios,
                    // ausente en entidades de dominio que también pueden tener .status)
                    if (resp && (resp as any).config !== undefined && typeof (resp as any).status === "number") {
                        resolve((resp as AxiosResponse<K>).data);
                    } else {
                        resolve(resp as K);
                    }
                }, reject);
            } else {
                resolve(promisable as K);
            }
        });
    }

    private showMessageOk(): void {
        this.snackbarRef.current("Operación realizada correctamente");
    }

    private showMessageErr(err: any): void {
        let causa = "[Revise las validaciones]";
        if (err?.response?.status >= 500) {
            causa = "[" + err.response.data.error + "]";
        } else if (Array.isArray(err?.response?.data)) {
            const errors: FormError[] = err.response.data;
            manageErrors(errors, this.formId);
        } else if (err?.response?.data?.field) {
            // Regla de negocio del servicio (FieldValidationException): llega como {error, field},
            // no como lista. Antes reventaba aquí y el usuario no veía bajo qué campo (#13718).
            const {field, error} = err.response.data;
            manageErrors([{code: "validation", field, defaultMessage: error}], this.formId);
            causa = "[" + error + "]";
        }
        this.snackbarRef.current("Error en la operación. " + causa, { variant: "error" });
    }
}

// ─── FormField: accesores tipados para campos del modelo ─────────────────────

export class FormField {
    constructor(
        readonly name: string,
        private readonly model: FormModel<any>,
    ) {}

    value(): any {
        return this.model.value(this.name);
    }

    valueAsBoolean(): boolean {
        return !!this.value();
    }

    valueAsDate(): string | null {
        const result = this.value();
        return result !== undefined ? result : null;
    }

    valueAsType(type: TypeConf<any>): any {
        if (type.isEnum) return this.value();
        const item = this.value();
        return item ? type.valuer(item) : null;
    }

    valueAsItem(asArray: boolean): any {
        let result: any = this.value();
        if (asArray && result && !result.map) {
            result = [result];
        }
        return result;
    }
}

export default FormModel;
